/* eslint-disable jira/naming/disallow-filenames */
// eslint-disable-next-line jira/filename-case
import React, { useMemo } from 'react';
import { styled } from '@compiled/react';
import { AvatarContent } from '@atlaskit/avatar/avatar-content';
import AvatarGroup, { type AvatarGroupProps } from '@atlaskit/avatar-group/avatar-group';
import type { AvatarProps, AvatarGroupOverrides } from '@atlaskit/avatar-group/types';
import { token } from '@atlaskit/tokens';
import { noop } from '../../utils.tsx';

const MAX_OVERFLOW_USER_COUNT_DISPLAYED = 99;
const OVERFLOW_USER_MARKER = '$$$overflow-user-marker';

type AvatarGroupAlignment = 'horizontal' | 'vertical';
type TooltipPosition = 'top' | 'bottom';
type Props = Omit<AvatarGroupProps, 'onMoreClick'> & {
	alignment: AvatarGroupAlignment;
	maxAvatarCount: number;
	tooltipPosition: TooltipPosition;
};

/*
 * The original AkAvatarGroup cannot be used inside an AkFlag, because the More button (e.g. `+2`)
 * opens a popup whose z-index is too low, and gets displayed below the flag.
 */
export const FlagCompatibleAvatarGroup = ({
	alignment,
	maxAvatarCount,
	tooltipPosition,
	data,
	...props
}: Props): React.JSX.Element => {
	const { data: customData, overrides: customOverrides } = useMemo(
		() => getCustomAvatarGroupProps({ data, maxAvatarCount }),
		[data, maxAvatarCount],
	);
	const avatarGroup = (
		<AvatarGroup
			maxCount={maxAvatarCount}
			data={customData}
			overrides={customOverrides}
			boundariesElement="scrollParent"
			/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */
			// @ts-ignore
			tooltipPosition={tooltipPosition}
			onMoreClick={noop} // Prevents the More popup. XXX: The popup has the wrong zIndex and displays under the flag
			{...props}
		/>
	);

	if (alignment === 'vertical') {
		return <VerticalAvatarGroupContainer>{avatarGroup}</VerticalAvatarGroupContainer>;
	}

	return avatarGroup;
};

export const getCustomAvatarGroupProps: ({
	data,
	maxAvatarCount,
}: Pick<AvatarGroupProps, 'data'> & {
	maxAvatarCount: number;
}) => {
	overrides: AvatarGroupOverrides;
	data: (
		| AvatarProps
		| {
				name: string;
				key: string;
		  }
	)[];
} = ({
	data: users,
	maxAvatarCount,
}: Pick<AvatarGroupProps, 'data'> & { maxAvatarCount: number }) => {
	const overflows = users.length > maxAvatarCount;
	const lastFeaturedUser = maxAvatarCount - 1;
	const featuredUsers = users.slice(0, lastFeaturedUser);
	const overflowUsers = users.slice(lastFeaturedUser);
	const overflowMarkerDummyUser = getOverflowMarkerDummyUser(overflowUsers);
	const processedUsers = overflows ? [...featuredUsers, overflowMarkerDummyUser] : users;
	const overrides: AvatarGroupProps['overrides'] = {
		Avatar: {
			render: (AvatarComponent, avatarProps) => {
				if (
					avatarProps.name === overflowMarkerDummyUser.name &&
					avatarProps.key === overflowMarkerDummyUser.key
				) {
					return renderAvatarWithOverflowCount(AvatarComponent, avatarProps, overflowUsers.length);
				}
				return <AvatarComponent {...avatarProps} />;
			},
		},
	};
	return { overrides, data: processedUsers };
};

export const getOverflowMarkerDummyUser = (
	overflowUsers: AvatarGroupProps['data'],
): {
	name: string;
	key: string;
} => ({
	name: overflowUsers.map((u) => u.name).join(', '),
	key: OVERFLOW_USER_MARKER,
});

type AvatarRender = NonNullable<
	NonNullable<NonNullable<AvatarGroupProps['overrides']>['Avatar']>['render']
>;
type AvatarRenderArgs = Parameters<AvatarRender>;

const renderAvatarWithOverflowCount = (
	AvatarComponent: AvatarRenderArgs[0],
	avatarProps: AvatarRenderArgs[1],
	overflowCount: number,
): ReturnType<AvatarRender> => (
	<AvatarComponent
		name={avatarProps.name}
		appearance={avatarProps.appearance}
		size={avatarProps.size}
		borderColor={avatarProps.borderColor}
		stackIndex={0}
	>
		<AvatarContent>
			<AvatarOverflowCountContainer>
				{}
				{`+${Math.min(overflowCount, MAX_OVERFLOW_USER_COUNT_DISPLAYED)}`}
			</AvatarOverflowCountContainer>
		</AvatarContent>
	</AvatarComponent>
);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const AvatarOverflowCountContainer = styled.span({
	width: '100%',
	height: '100%',
	display: 'grid',
	placeItems: 'center',
	backgroundColor: token('color.background.neutral'),
	color: token('color.text'),
	textAlign: 'center',
	fontWeight: token('font.weight.medium'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const VerticalAvatarGroupContainer = styled.span({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'> ul': {
		flexDirection: 'column',
		marginRight: 0,
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'> ul > li': {
		marginRight: 0,
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
		'&:not(:first-of-type)': {
			marginTop: token('space.negative.100'),
		},
	},
});
